const hiddenClass = 'selling_plan_theme_integration--hidden';

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.enablePerformanceObserver();
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.listenToAllSellingPlanFormRadioButtonChanges();
    this.interceptFormSubmit();
    this.updatePrice();
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute('data-section-id');
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

   /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */

  get variantIdInput() {
    const allInputs = this.shopifySection.querySelectorAll('input[name="id"], select[name="id"]');
    for (const input of allInputs) {
      if (!input.disabled && input.offsetParent !== null) {
        return input;
      }
    }
    return allInputs[0] || null;
  }

  get priceElement() {
    return this.shopifySection.querySelector('.product-price-group');
  }

  get comparedAtPrice() {
    return this.shopifySection.querySelector('.price__sale');
  }

  get visibleSellingPlanForm() {
    const currentVariantId = this.variantIdInput?.value;
    const form = this.sellingPlansWidgetContainer.querySelector(
      `section[data-variant-id="${currentVariantId}"]`,
    );
    return form;
  }

  get isVariantAvailable() {
    return this.selectedPurchaseOption?.getAttributeNames().includes('disabled');
  }

  get sellingPlanInput() {
    return this.shopifySection.querySelector('.selected-selling-plan-id');
  }

  get addToCartForms() {
    return this.shopifySection.querySelectorAll('[action*="/cart/add"]');
  }

    /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */

  appendSellingPlanInputs() {
    this.addToCartForms.forEach((addToCartForm) => {
      const clonedInput = this.sellingPlanInput.cloneNode();
      clonedInput.removeAttribute('disabled');
      addToCartForm.appendChild(clonedInput);
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(hiddenClass);
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add(hiddenClass);
    });
  }

    /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */

  handleSellingPlanFormVisibility() {
    const currentVariantId = this.variantIdInput.value;
    
    const sellingPlanFormForSelectedVariant = this.sellingPlansWidgetContainer.querySelector(
      `section[data-variant-id="${currentVariantId}"]`,
    );
    const sellingPlanFormsForUnselectedVariants =
      this.sellingPlansWidgetContainer.querySelectorAll(
        `.selling_plan_theme_integration:not([data-variant-id="${currentVariantId}"])`,
      );
    
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
    
    if (sellingPlanFormForSelectedVariant) {
      const firstRadio = sellingPlanFormForSelectedVariant.querySelector('input[type="radio"]');
      if (firstRadio && !sellingPlanFormForSelectedVariant.querySelector('input[type="radio"]:checked')) {
        firstRadio.checked = true;

      }
    }
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    setTimeout(() => {
      this.updateSellingPlanInputsValues();
      this.listenToAllSellingPlanFormRadioButtonChanges();
    }, 50);
  }

    /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */

  listenToVariantChange() {
    const sectionObserver = new MutationObserver(() => {
      const currentVariantInput = this.variantIdInput;
      if (currentVariantInput && this._lastVariantInput !== currentVariantInput) {

        this._lastVariantInput = currentVariantInput;
        this.handleVariantChange();
      }
    });

    sectionObserver.observe(this.shopifySection, { childList: true, subtree: true });

    const currentVariantInput = this.variantIdInput;
    if (currentVariantInput) {
      currentVariantInput.addEventListener('change', () => {

        this.handleVariantChange();
      });

      const variantIdObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.attributeName === 'value') {

            this.handleVariantChange();
          }
        }
      });
      variantIdObserver.observe(currentVariantInput, { attributes: true, attributeFilter: ['value'] });
    }
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener('change', (e) => {
        if (e.target.name === 'id' || e.target.getAttribute('name') === 'id') {

          this.handleVariantChange();
        }
      });
    });
  }

  get regularPriceElement() {
    return this.shopifySection.querySelector('.price__regular');
  }

  get salePriceElement() {
    return this.shopifySection.querySelector('.price__sale');
  }

  get salePriceValue() {
    return this.salePriceElement?.querySelector('.price-item--sale');
  }

  get regularPriceValue() {
    return this.salePriceElement?.querySelector('.price-item--regular');
  }

  get sellingPlanAllocationPrice() {
    return document.getElementById(
      `${this.selectedPurchaseOption?.dataset.sellingPlanGroupId}_allocation_price`,
    );
  }

  get selectedPurchaseOptionPrice() {
    return this.selectedPurchaseOption?.dataset.variantPrice;
  }

  get selectedPurchaseOptionComparedAtPrice() {
    return this.selectedPurchaseOption?.dataset.variantCompareAtPrice;
  }

  get price() {
    return this.sellingPlanAllocationPrices?.price ?? null;
  }

    /*
    We aim to ascertain whether a compared price exists, which would indicate that the currently selected input has a discount applied to it.
    If a discount is detected, the discounted price is displayed; otherwise, the regular price is shown.
  */

  updatePrice() {
    if (!this.selectedPurchaseOption) return;
    
    if (
      !this.selectedPurchaseOptionComparedAtPrice ||
      this.selectedPurchaseOptionComparedAtPrice ===
        this.selectedPurchaseOptionPrice
    ) {
      this.showRegularPrice();
      this.hideSalePrice();
    } else {
      this.showSalePrice();
      this.hideRegularPrice();
      this.priceElement?.classList.add('price--on-sale');
    }
  }

  hideSalePrice() {
    if (this.salePriceElement) {
      this.salePriceElement.style.display = 'none';
    }
  }

  hideRegularPrice() {
    if (this.regularPriceElement) {
      this.regularPriceElement.style.display = 'none';
    }
  }

  showRegularPrice() {
    if (this.regularPriceElement) {
      this.regularPriceElement.style.display = 'block';
    }
    const saleElement = this.shopifySection?.querySelector('.price__sale');
    if (saleElement) {
      saleElement.style.display = 'none';
    }
  }

  showSalePrice() {
    if (this.salePriceElement) {
      this.salePriceElement.style.display = 'block';
    }
    if (this.regularPriceValue) {
      this.regularPriceValue.innerHTML = this.selectedPurchaseOptionComparedAtPrice;
    }
    if (this.salePriceValue) {
      this.salePriceValue.innerHTML = this.selectedPurchaseOptionPrice;
    }
  }

  get sellingPlanInputs() {
    return this.shopifySection.querySelectorAll('input[name="selling_plan"]');
  }

  updateSellingPlanInputsValues() {
    const value = this.sellingPlanInputValue;
    
    
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.removeAttribute('disabled');
      sellingPlanInput.value = value || '';
    
    });
  }

  get sellingPlanInputValue() {
    const selected = this.selectedPurchaseOption;

    if (selected?.dataset.radioType === 'one_time_purchase') {
      return null;
    }
    return selected?.dataset.sellingPlanId ?? null;
  }

  get selectedPurchaseOption() {
    const option = this.visibleSellingPlanForm?.querySelector(
      'input[type="radio"]:checked',
    );
    return option;
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  handleRadioButtonChange(selectedPurchaseOption) {

    const clickedVariantId = selectedPurchaseOption.dataset.variantId;
    
    if (clickedVariantId && clickedVariantId !== this.variantIdInput.value) {
      
      if (this.variantIdInput.tagName === 'SELECT') {
        this.variantIdInput.value = clickedVariantId;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.variantIdInput.value = clickedVariantId;
        this.variantIdInput.setAttribute('value', clickedVariantId);
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      setTimeout(() => {
        const newVisibleForm = this.visibleSellingPlanForm;
        const radioInNewForm = newVisibleForm?.querySelector(
          `input[data-radio-type="${selectedPurchaseOption.dataset.radioType}"][data-selling-plan-id="${selectedPurchaseOption.dataset.sellingPlanId || ''}"]`
        );
        
        if (radioInNewForm) {
          radioInNewForm.checked = true;
        }
        
        this.updateSellingPlanInputsValues();
        this.updatePrice();
      }, 100);
    } else {
   
      this.selectedPurchaseOption = selectedPurchaseOption;
      this.updateSellingPlanInputsValues();
      this.updatePrice();
    }
  }

  listenToAllSellingPlanFormRadioButtonChanges() {
    const allForms = this.sellingPlansWidgetContainer.querySelectorAll('.selling_plan_theme_integration');
    
    allForms.forEach((form) => {
      const radios = form.querySelectorAll('input[type="radio"]');
      
      radios.forEach((radio) => {
        const newRadio = radio.cloneNode(true);
        radio.parentNode.replaceChild(newRadio, radio);
        
        newRadio.addEventListener('change', (event) => {
          this.handleRadioButtonChange(event.target);
        });
      });
    });
  }

  interceptFormSubmit() {
    this.addToCartForms.forEach((form) => {
      form.addEventListener('submit', (e) => {

        this.updateSellingPlanInputsValues();
        
        const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
        
        if (sellingPlanInput) {
          const correctValue = this.sellingPlanInputValue || '';
          sellingPlanInput.removeAttribute('disabled');
          sellingPlanInput.value = correctValue;

        }
      });
      
      const submitButtons = form.querySelectorAll('[type="submit"], button[name="add"]');
      submitButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          setTimeout(() => {
            this.updateSellingPlanInputsValues();
            
            const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
            const correctValue = this.sellingPlanInputValue || '';
            
            if (sellingPlanInput) {
              sellingPlanInput.removeAttribute('disabled');
              sellingPlanInput.value = correctValue;
            }
          }, 10);
        });
      });
    });
  }

  enablePerformanceObserver() {
    const performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType !== 'fetch') return;

        const url = new URL(entry.name);
        /*
          When a buyer selects a product variant, a fetch request is initiated.
          Upon completion of this fetch request, we update the price to reflect the correct value.
        */

        if (url.search.includes('variant') || url.search.includes('variants')) {
          this.updatePrice();
        }
      });
    });

    performanceObserver.observe({entryTypes: ['resource']});
  }
}

document
  .querySelectorAll('.selling_plan_app_container')
  .forEach((sellingPlansWidgetContainer) => {
    new SellingPlansWidget(sellingPlansWidgetContainer);
  });
